/**
 * API client functions for the Deploy Service.
 * Handles triggering deployments and querying deployment status.
 */

import { apiClient } from '@/modules/api/api-client';

// --- Types ---

export interface DeployArtifactRequest {
  projectKey: string;
  environment: string;
  artifactId: string;
  version?: string;
  deploymentType?: string;
  server?: string;
  testRun?: boolean;
  verbose?: 'none' | 'low' | 'high' | 'extreme';
}

export interface DeployArtifactResponse {
  runId: string;
  status: 'QUEUED';
}

export interface DeploymentSummary {
  runId: string;
  type: string;
  status: 'QUEUED' | 'RUNNING' | 'SUCCESS' | 'FAILED';
  projectKey?: string;
  environment?: string;
  user: string;
  displayName?: string;
  testRun?: boolean;
  started: string;
  completed?: string;
  version?: string;
  artifactId?: string;
  assetLabel?: string;
}

// --- Deploy APIs ---

export async function triggerArtifactDeploy(
  request: DeployArtifactRequest
): Promise<DeployArtifactResponse> {
  return apiClient.post<DeployArtifactResponse>('/deployments/artifact', request);
}

export async function getDeploymentStatus(runId: string): Promise<DeploymentSummary> {
  return apiClient.get<DeploymentSummary>(
    `/deployments/${encodeURIComponent(runId)}/status`
  );
}

export interface PagedDeployments {
  content: DeploymentSummary[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export async function getArtifactStatuses(projectKey: string): Promise<DeploymentSummary[]> {
  return apiClient.get<DeploymentSummary[]>(
    `/deployments/artifact-statuses?projectKey=${encodeURIComponent(projectKey)}`
  );
}

export async function listDeployments(params?: {
  projectKey?: string;
  environment?: string;
  status?: string;
  type?: string;
  excludeTypes?: string;
  excludeRunIdPrefixes?: string;
  artifactId?: string;
  page?: number;
  size?: number;
}): Promise<PagedDeployments> {
  const searchParams = new URLSearchParams();
  if (params?.projectKey) searchParams.set('projectKey', params.projectKey);
  if (params?.environment) searchParams.set('environment', params.environment);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.type) searchParams.set('type', params.type);
  if (params?.excludeTypes) searchParams.set('excludeTypes', params.excludeTypes);
  if (params?.excludeRunIdPrefixes) searchParams.set('excludeRunIdPrefixes', params.excludeRunIdPrefixes);
  if (params?.artifactId) searchParams.set('artifactId', params.artifactId);
  if (params?.page != null) searchParams.set('page', String(params.page));
  if (params?.size != null) searchParams.set('size', String(params.size));
  const qs = searchParams.toString();
  return apiClient.get(`/deployments${qs ? `?${qs}` : ''}`);
}
export interface DeployConfigRequest {
  projectKey: string;
  environment: string;
  configFileIds: string[];
  versionPath: string;
  version: string;
  testRun?: boolean;
  verbose?: 'none' | 'low' | 'high' | 'extreme';
}

export async function triggerConfigDeploy(
  request: DeployConfigRequest
): Promise<DeployArtifactResponse> {
  return apiClient.post<DeployArtifactResponse>('/deployments/configuration', request);
}

// --- Server Management Types ---

export type ServerAction = 'START' | 'STOP' | 'REBOOT' | 'CHECK';

export interface ManageServerRequest {
  projectKey: string;
  environment: string;
  serverId: string;
  action: ServerAction;
  testRun?: boolean;
  verbose?: 'none' | 'low' | 'high' | 'extreme';
}

// --- Service Instance Management Types ---

export type ServiceAction = 'START' | 'STOP' | 'RESTART' | 'CHECK';

export interface ManageServiceRequest {
  projectKey: string;
  environment: string;
  serviceInstanceIds: string[];
  action: ServiceAction;
  serverId?: string;
  testRun?: boolean;
  verbose?: 'none' | 'low' | 'high' | 'extreme';
}

// --- Server & Service Management APIs ---

export async function manageServer(
  request: ManageServerRequest
): Promise<DeployArtifactResponse> {
  return apiClient.post<DeployArtifactResponse>('/deployments/server', request);
}

export async function manageService(
  request: ManageServiceRequest
): Promise<DeployArtifactResponse> {
  return apiClient.post<DeployArtifactResponse>('/deployments/service', request);
}

// --- Deployment Detail (with log output) ---

export interface DeploymentDetail {
  runId: string;
  type: string;
  status: 'QUEUED' | 'RUNNING' | 'SUCCESS' | 'FAILED';
  projectKey?: string;
  environment?: string;
  user: string;
  displayName?: string;
  testRun: boolean;
  command?: string;
  log?: string;
  started: string;
  completed?: string;
  version?: string;
  artifactId?: string;
  assetLabel?: string;
}

export async function getDeploymentDetail(runId: string): Promise<DeploymentDetail> {
  return apiClient.get<DeploymentDetail>(
    `/deployments/${encodeURIComponent(runId)}`
  );
}

// --- Database Script Execution ---

export interface RunDbScriptRequest {
  projectKey: string;
  environment: string;
  databaseId: string;
  script: string;
  testRun?: boolean;
  verbose?: 'none' | 'low' | 'high' | 'extreme';
}

export async function runDbScript(
  request: RunDbScriptRequest
): Promise<DeployArtifactResponse> {
  return apiClient.post<DeployArtifactResponse>('/deployments/db-script', request);
}

// --- Config Deployment Status Types & APIs ---

/**
 * Represents the last deployment status for a config file deployment
 * within a specific project/environment combination.
 */
export interface ConfigDeployStatusData {
  lastSuccess?: DeployedConfigStatus;
}

/**
 * Map of environmentId → last deployment status data for config files.
 */
export type ConfigDeployStatusMap = Record<string, ConfigDeployStatusData>;

/**
 * Fetches environment-scoped deployment statuses for configuration file deployments.
 * Returns the most recent deployment record per environment within the given project.
 * Routed to the Configuration Service (deployed_config_files table).
 */
export async function getConfigDeployStatuses(
  projectKey: string
): Promise<DeployedConfigStatus[]> {
  return apiClient.get<DeployedConfigStatus[]>(
    `/projects/${encodeURIComponent(projectKey)}/deployed-config-statuses`
  );
}

/**
 * A deployed config file record returned by the configuration service.
 * Represents the latest deployment for a given environment.
 */
export interface DeployedConfigStatus {
  id: number;
  configFileId: string;
  environmentId: string;
  projectKey: string;
  version: string;
  deployedAt: string;
}
